import { useContext, useState } from "react";
import { STRINGS } from "../constant";
import { IMAGES } from "../assets";
import { Header, LAView, LAText } from "../components";
import { useNavigate } from "react-router-dom";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";
import { AuthContext } from "../context/AuthProvider";
import BackArrowIcon from "../components/BackArrowIcon";
import { ThemeContext } from "../context/ThemeProvider";
import { SHOW_TOAST } from "../constant/utils";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, 
  faArrowLeft, 
  faSave,
  faCog
} from '@fortawesome/free-solid-svg-icons';

function TestSetting() {
  const { bundleData, profile, community, setTestOptions, setFontsOptions } =
    useContext(AuthContext);
  const initialTime = profile?.time_per_question
    ? profile?.time_per_question
    : community?.total_time_per_question ?? [60, 90];
  const intialGoback = profile?.allow_go_back ?? true;

  const [isGoback, setGoback] = useState(intialGoback);
  const [time, setTime] = useState(initialTime);
  const [isChecked, setIsChecked] = useState(false);
  const { theme, currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  
  // Card animations
  const cardVariants = {
    hover: { 
      scale: 1.02,
      boxShadow: "0px 8px 15px rgba(0,0,0,0.1)",
      transition: { type: "spring", stiffness: 400, damping: 10 }
    }
  };

  // Helper function to get icon background color
  const getIconBg = () => {
    return currentTheme === 'light' ? theme?.icon_bg?.profile_light : theme?.icon_bg?.profile_dark;
  }

  // Helper function to get card background
  const getCardBackground = (iconBg) => {
    const baseColor = currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark;
    const darkerShade = currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark; 
    return {
      background: `linear-gradient(to right, ${baseColor}, ${darkerShade})`,
      borderLeft: `4px solid ${iconBg || (currentTheme === 'light' ? theme?.icon_bg?.profile_light : theme?.icon_bg?.profile_dark)}`
    };
  };

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  const showRangeValue = (e) => {
    setTime(e[1]);
  };

  const prevQuestionSetting = (e) => {
    setGoback(e.target.checked);
  };

  const handleSave = () => {
    setTestOptions(time, isGoback);
    SHOW_TOAST("Test Options Updated.", "success");
    navigate(-1);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          className="rounded-lg border-2 p-2 mr-4 flex items-center justify-center"
          style={{ borderColor: currentTheme === "light" ? theme?.border_color?.light : theme?.border_color?.dark }}
          onClick={() => navigate(-1)}
        >
          <BackArrowIcon
            color={currentTheme === "light" ? theme?.text?.light : theme?.text?.dark}
          />
        </button>
        <div>
          <h1 
            className="text-2xl font-semibold"
            style={{ color: getTextColor() }}
          >
            Settings
          </h1>
          <p 
            className="text-sm text-gray-500 dark:text-gray-400 mt-1"
          >
            Test Options
          </p>
        </div>
      </div>

      {/* Settings Card */}
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm"
        whileHover="hover"
        variants={cardVariants}
      >
        <div style={getCardBackground(getIconBg())}>
          <div className="p-6">
            <div className="space-y-8">
              {/* Time per Question */}
              <div>
                <div className="flex items-center mb-4">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center mr-3"
                    style={{ backgroundColor: getIconBg() }}
                  >
                    <FontAwesomeIcon 
                      icon={faClock} 
                      className="text-white" 
                    />
                  </div>
                  <h2 
                    className="text-lg font-medium"
                    style={{ color: getTextColor() }}
                  >
                    Time per Question ({time[1] || time} seconds)
                  </h2>
                </div>
                
                <div className="pl-2 pr-2 py-4">
                  <RangeSlider
                    onInput={(e) => showRangeValue(e)}
                    className="single-thumb"
                    value={(time && [60, time]) || [60, time]}
                    min="60"
                    max="300"
                    thumbsDisabled={[true, false]}
                    rangeSlideDisabled={true}
                  />
                  <div className="flex justify-between mt-2 text-sm text-gray-500 dark:text-gray-400">
                    <span>60s</span>
                    <span>120s</span>
                    <span>180s</span>
                    <span>240s</span>
                    <span>300s</span>
                  </div>
                </div>
              </div>

              {/* Allow Going Back */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center mr-3"
                    style={{ backgroundColor: getIconBg() }}
                  >
                    <FontAwesomeIcon 
                      icon={faArrowLeft} 
                      className="text-white" 
                    />
                  </div>
                  <h2 
                    className="text-lg font-medium"
                    style={{ color: getTextColor() }}
                  >
                    Allow go back to previous Questions?
                  </h2>
                </div>
                
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isGoback}
                    onChange={prevQuestionSetting}
                    className="sr-only peer"
                  />
                  <div 
                    className="w-14 h-7 rounded-full peer bg-gray-200 dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all"
                    style={{ 
                      backgroundColor: isGoback ? getIconBg() : currentTheme === 'light' ? 'rgba(229,231,235,1)' : 'rgba(55,65,81,1)'
                    }}
                  ></div>
                </label>
              </div>

              {/* Save Button */}
              <div className="pt-6 flex justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSave}
                  className="flex items-center px-6 py-3 rounded-lg font-medium text-white"
                  style={{ backgroundColor: getIconBg() }}
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  Save
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default TestSetting;